import { useEffect, useRef, ReactNode } from "react";

/**
 * Mobile-friendly auto-scrolling marquee that also supports
 * native touch/drag interaction. Children should be the duplicated
 * list of slides (rendered twice for seamless loop).
 *
 * - Auto-scrolls slowly to the right
 * - Pauses while the user touches/drags
 * - Resumes ~1.4s after release
 * - Loops seamlessly by jumping when scrollLeft passes half of track width
 */
type Props = {
  children: ReactNode;
  className?: string;
  /** pixels per second */
  speed?: number;
  /** outer container style overrides (mask etc.) */
  style?: React.CSSProperties;
  /** inner track style overrides (gap, padding) */
  trackStyle?: React.CSSProperties;
};

const DragMarquee = ({
  children,
  className,
  speed = 28,
  style,
  trackStyle,
}: Props) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const resumeTimer = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);
  const lastTs = useRef<number | null>(null);

  useEffect(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;

    // Wait a tick so children measure correctly
    const start = () => {
      const tick = (ts: number) => {
        if (!scroller || !track) return;
        if (lastTs.current == null) lastTs.current = ts;
        const dt = (ts - lastTs.current) / 1000;
        lastTs.current = ts;

        if (!pausedRef.current) {
          const half = track.scrollWidth / 2;
          let next = scroller.scrollLeft + speed * dt;
          if (next >= half) next -= half;
          scroller.scrollLeft = next;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const onTouchStart = () => {
      pausedRef.current = true;
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
    };
    const onTouchEnd = () => {
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
      resumeTimer.current = window.setTimeout(() => {
        pausedRef.current = false;
        lastTs.current = null;
      }, 1400);
    };
    // Loop-correct after user scrolls (native overscroll)
    const onScroll = () => {
      const half = track.scrollWidth / 2;
      if (scroller.scrollLeft >= half) {
        scroller.scrollLeft -= half;
      } else if (scroller.scrollLeft < 0) {
        scroller.scrollLeft += half;
      }
    };

    scroller.addEventListener("touchstart", onTouchStart, { passive: true });
    scroller.addEventListener("touchend", onTouchEnd, { passive: true });
    scroller.addEventListener("touchcancel", onTouchEnd, { passive: true });
    scroller.addEventListener("scroll", onScroll, { passive: true });

    start();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (resumeTimer.current) window.clearTimeout(resumeTimer.current);
      scroller.removeEventListener("touchstart", onTouchStart);
      scroller.removeEventListener("touchend", onTouchEnd);
      scroller.removeEventListener("touchcancel", onTouchEnd);
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [speed]);

  return (
    <div
      ref={scrollerRef}
      className={className}
      style={{
        overflowX: "auto",
        overflowY: "hidden",
        WebkitOverflowScrolling: "touch",
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        ...style,
      }}
    >
      <div
        ref={trackRef}
        style={{
          display: "flex",
          width: "max-content",
          willChange: "transform",
          ...trackStyle,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export default DragMarquee;
